import React, { useState } from 'react';
import clsx from 'clsx';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { ReactComponent as TransferIcon } from 'images/transfer-rounded.svg';
import CheckboxSmall, { CheckboxSize } from 'components/Checkbox/CheckboxSmall';
import { Tradeline } from 'handlers/applicationData';
import { SelectedTradeline, TradelineNewState } from 'api/ApplicationDataApi';
import { isDirectPay } from 'enums/MethodTypes';

import EditTradelinePayoffAmountModal from 'components/SelectDebts/components/EditTradelinePayoffAmountModal';

import styles from './TradeLineItem.module.scss';

interface TradeLineItemProps {
  tradeline: Tradeline;
  disabled?: boolean;
  maxLoanAmount: number;
  currentTotalConsolidated: number;
  onSelect?: (selectedTradeline: SelectedTradeline) => void;
  updateTradelinePayoffAmount?: (tradelineId: string, amount: number) => void;
}

const getSavingsAmountLabel = (
  savings: number | null | undefined,
  monthlyPaymentSavings: number | null | undefined,
  remainingMonths: number | null | undefined,
) => {
  savings = Math.max(savings ?? 0, 0);
  monthlyPaymentSavings = Math.max(monthlyPaymentSavings ?? 0, 0);

  if (!savings && !monthlyPaymentSavings) {
    return null;
  }

  if (savings > monthlyPaymentSavings * (remainingMonths ?? 0)) {
    return formatMonetaryAmount(savings);
  }

  return `${formatMonetaryAmount(monthlyPaymentSavings)}/mo`;
};

export default function TradeLineItem({
  tradeline,
  disabled,
  maxLoanAmount,
  currentTotalConsolidated,
  onSelect,
  updateTradelinePayoffAmount,
}: TradeLineItemProps) {
  const {
    balanceToConsolidate,
    balance,
    id,
    methodCapabilities,
    firm,
    selectedForConsolidation,
    savings,
    monthlyPaymentSavings,
    remainingMonths,
  } = tradeline;
  const savingsAmount = selectedForConsolidation
    ? getSavingsAmountLabel(savings, monthlyPaymentSavings, remainingMonths)
    : undefined;
  const payOffAmount = balanceToConsolidate;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleUpdate = (amount: number) => {
    updateTradelinePayoffAmount?.(id, amount);
    setIsModalOpen(false);
  };

  const renderTradeLineValue = () => {
    if (!selectedForConsolidation) return <p className={styles.noPaymentText}>{formatMonetaryAmount(balance)}</p>;

    if (payOffAmount < balance) {
      return (
        <p className={styles.partialPaymentText}>
          <span className={styles.totalPaymentText}>{formatMonetaryAmount(payOffAmount)}</span> of{' '}
          {formatMonetaryAmount(balance)}
        </p>
      );
    }

    return <p className={styles.totalPaymentText}>{formatMonetaryAmount(payOffAmount)}</p>;
  };

  const isChecked = !!selectedForConsolidation;

  const handleToggle = () => {
    onSelect?.({
      tradeline,
      newState: isChecked ? TradelineNewState.Unselected : TradelineNewState.Selected,
    });
  };

  const handleEdit = (e: React.MouseEvent<HTMLParagraphElement>) => {
    // Stop propagation of the event to prevent the checkbox from being toggled
    e.stopPropagation();
    if (disabled) return;
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        className={clsx(styles.container, disabled && styles.containerDisabled)}
        onClick={(e) => {
          if (disabled) return;
          // prevent triggering checkbox change event
          e.preventDefault();

          // prevent opening the expander if TradelineItem is clicked
          e.stopPropagation();
          handleToggle();
        }}
      >
        {onSelect && (
          <CheckboxSmall
            disabled={disabled}
            className={styles.checkbox}
            size={CheckboxSize.Sm}
            checkboxLabelClassName={styles.checkboxLabel}
            checked={isChecked}
            onChange={handleToggle}
          />
        )}
        <div className={styles.tradeline}>
          <p className={styles.label}>
            {isDirectPay(methodCapabilities) && <TransferIcon className={styles.transferIcon} />}
            {firm}
          </p>
          {savingsAmount ? <p className={styles.savingsAmount}>Save {savingsAmount}</p> : undefined}
        </div>
        {renderTradeLineValue()}
        {onSelect && (
          <p className={styles.editButton} onClick={handleEdit}>
            Edit
          </p>
        )}
      </div>

      <EditTradelinePayoffAmountModal
        isOpen={isModalOpen}
        firm={firm}
        balance={balance}
        initialAmount={balanceToConsolidate}
        maxLoanAmount={maxLoanAmount}
        currentTotalConsolidated={currentTotalConsolidated}
        onClose={() => setIsModalOpen(false)}
        onUpdate={handleUpdate}
        // We use the key to force the modal to re-render when it is opened so it initialies with updated values
        key={isModalOpen.toString()}
      />
    </>
  );
}
